import { FilterOutlined, UserOutlined } from '@ant-design/icons'
import { Avatar, Button, Checkbox, Flex, Popover, Typography } from 'antd'
import { memo, useCallback, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import CantileverTheme from 'styles/cantileverTheme'
import { displayNameFromEmail } from 'utils/stringHelpers'

const { Text } = Typography

function getInitials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/)
  if (parts.length >= 2)
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return (parts[0]?.[0] ?? '?').toUpperCase()
}

const AVATAR_COLORS = [
  CantileverTheme.colors.blue['400'],
  CantileverTheme.colors.purple['500_base'],
  CantileverTheme.colors.crimson['400'],
  CantileverTheme.colors.alert['400_base'],
  CantileverTheme.colors.success['400_base'],
  CantileverTheme.colors.Turquoise,
  CantileverTheme.colors.purple['600'],
  CantileverTheme.colors.gold['500_base'],
]

function emailToColor(email: string): string {
  let hash = 0
  for (let i = 0; i < email.length; i++) {
    hash = ((hash << 5) - hash + email.charCodeAt(i)) | 0
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]
}

interface CreatorFilterButtonProps {
  emails: string[]
  selectedEmails: string[]
  onChange: (emails: string[], id?: string) => void
  id?: string
  compact?: boolean
}

interface AvatarCircleProps {
  email: string
  size?: number
}

const AvatarCircle = memo(function AvatarCircle({
  email,
  size = 24,
}: AvatarCircleProps) {
  const label = displayNameFromEmail(email)
  const initials = getInitials(label)
  const color = emailToColor(email)
  return (
    <span title={label}>
      <Avatar
        size={size}
        style={{
          backgroundColor: color,
          fontSize: Math.max(size * 0.42, 9),
          fontWeight: 600,
          lineHeight: 1,
          flexShrink: 0,
        }}>
        {initials}
      </Avatar>
    </span>
  )
})

interface PopoverContentProps {
  emails: string[]
  selectedSet: ReadonlySet<string>
  onToggle: (email: string) => void
  onClear: () => void
}

const PopoverContent = memo(function PopoverContent({
  emails,
  selectedSet,
  onToggle,
  onClear,
}: PopoverContentProps) {
  return (
    <Flex vertical style={{ minWidth: 200, maxHeight: 280, overflowY: 'auto' }}>
      {selectedSet.size > 0 && (
        <Button
          type='link'
          size='small'
          onClick={onClear}
          style={{
            alignSelf: 'flex-start',
            padding: '6px 12px',
            fontSize: '0.75rem',
          }}>
          Clear all
        </Button>
      )}
      {emails.map((email) => {
        const label = displayNameFromEmail(email)
        return (
          <CreatorRow key={email} onClick={() => onToggle(email)}>
            <Checkbox checked={selectedSet.has(email)} />
            <AvatarCircle email={email} size={22} />
            <Text ellipsis style={{ fontSize: '0.8rem' }}>
              {label}
            </Text>
          </CreatorRow>
        )
      })}
      {emails.length === 0 && (
        <Text
          type='secondary'
          style={{ padding: 12, fontSize: '0.75rem', textAlign: 'center' }}>
          No creators
        </Text>
      )}
    </Flex>
  )
})

export const CreatorFilterButton = memo(function CreatorFilterButton({
  emails,
  selectedEmails,
  onChange,
  id,
  compact = false,
}: CreatorFilterButtonProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedSet = useMemo(() => new Set(selectedEmails), [selectedEmails])

  const handleToggle = useCallback(
    (email: string) => {
      const next = selectedSet.has(email)
        ? selectedEmails.filter((e) => e !== email)
        : [...selectedEmails, email]
      onChange(next, id)
    },
    [selectedEmails, selectedSet, onChange, id]
  )

  const handleClear = useCallback(() => {
    onChange([], id)
  }, [onChange, id])

  const selectedList = useMemo(
    () => emails.filter((e) => selectedSet.has(e)),
    [emails, selectedSet]
  )

  const hasSelection = selectedSet.size > 0

  const avatarStack = (avatarSize: number) => (
    <Flex align='center'>
      {selectedList.slice(0, 3).map((email, i) => (
        <div
          key={email}
          style={{
            marginLeft: i === 0 ? 0 : -6,
            zIndex: 3 - i,
            position: 'relative',
          }}>
          <AvatarCircle email={email} size={avatarSize} />
        </div>
      ))}
      {selectedList.length > 3 && (
        <Text
          type='secondary'
          style={{ marginLeft: 2, fontSize: '0.7rem', fontWeight: 600 }}>
          +{selectedList.length - 3}
        </Text>
      )}
    </Flex>
  )

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger='click'
      placement='bottomLeft'
      arrow={false}
      content={
        <PopoverContent
          emails={emails}
          selectedSet={selectedSet}
          onToggle={handleToggle}
          onClear={handleClear}
        />
      }
      getPopupContainer={() => containerRef.current ?? document.body}>
      <div
        ref={containerRef}
        style={{ position: 'relative', display: 'inline-flex' }}>
        {compact ? (
          <CompactTrigger $active={hasSelection}>
            {hasSelection ? avatarStack(20) : <UserOutlined />}
          </CompactTrigger>
        ) : (
          <SelectTrigger $active={hasSelection}>
            <Flex
              align='center'
              style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              {hasSelection ? (
                avatarStack(20)
              ) : (
                <Text type='secondary' style={{ fontSize: 14 }}>
                  Created by
                </Text>
              )}
            </Flex>
            <FilterOutlined
              style={{
                color: CantileverTheme.colors.gray[700],
                fontSize: 12,
                marginLeft: 4,
                flexShrink: 0,
              }}
            />
          </SelectTrigger>
        )}
      </div>
    </Popover>
  )
})

const SelectTrigger = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  width: 200px;
  height: 100%;
  padding: 0 11px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.colors.gray[600]};
  background: transparent;
  color: ${({ theme }) => theme.forecast.activeText};
  cursor: pointer;
  font-size: 14px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
  }
`

const CompactTrigger = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  height: 26px;
  border-radius: 6px;
  border: 1px solid
    ${({ theme, $active }) =>
      $active ? theme.forecast.primary : theme.colors.gray[600]};
  background: ${({ theme, $active }) =>
    $active ? `${theme.forecast.primary}18` : theme.colors.gray[700]};
  color: ${({ theme }) => theme.forecast.activeText};
  cursor: pointer;
  font-size: 0.7rem;
  transition: all 0.15s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${({ theme }) => theme.forecast.primary};
    background: ${({ theme }) => `${theme.forecast.primary}18`};
  }
`

const CreatorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: ${({ theme }) => theme.colors.gray[700]};
  }
`
